import { PageHero } from '@components/common';
import { ArrowRight, MapPin, Clock, CheckCircle2, Briefcase } from 'lucide-react';

export default function FractionalCISOPage() {
  return (
    <>
      <PageHero
        title="Fractional CISO"
        eyebrow="Careers"
        seoTitle="Fractional CISO | The CISO Network"
        seoDescription="Join our network as a Fractional CISO. Help businesses build world-class security programmes on a flexible, part-time basis."
      />

      {/* Role Overview */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4 mb-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              Remote UK
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Contract
            </span>
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4" />
              10+ years experience
            </span>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                About the Role
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-muted-foreground mb-4">
                  We're seeking experienced CISOs to join our elite network of fractional security
                  leaders. As a Fractional CISO, you'll work with multiple growing businesses,
                  providing strategic security leadership on a part-time basis.
                </p>
                <p className="text-muted-foreground mb-4">
                  This is not a typical consulting role. You'll embed deeply with client
                  organisations, attending board meetings, building security programmes from the
                  ground up, and becoming a trusted advisor to executive teams.
                </p>
                <p className="text-muted-foreground mb-8">
                  Our clients range from Series A startups preparing for enterprise sales to
                  established mid-market companies undergoing digital transformation. Each
                  engagement brings new challenges and the opportunity to make a meaningful impact.
                </p>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  Key Responsibilities
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    'Develop and implement comprehensive security strategies aligned with business objectives',
                    'Build and mentor client security teams, establishing sustainable security capabilities',
                    'Lead board-level security discussions and translate technical risks into business terms',
                    'Design and implement security governance frameworks, policies, and procedures',
                    'Manage security incidents and crisis response when required',
                    'Guide clients through compliance certifications (SOC 2, ISO 27001, etc.)',
                    'Conduct security assessments and develop risk treatment plans',
                    'Collaborate with The CISO Network team on methodology and best practices',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  What We're Looking For
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    '10+ years of cybersecurity experience with at least 3 years in a CISO or equivalent leadership role',
                    'Track record of building security programmes from scratch in growing organisations',
                    'Strong stakeholder management skills and ability to influence at board level',
                    'Deep understanding of security frameworks (ISO 27001, SOC 2, NIST CSF)',
                    'Experience with cloud security (AWS, Azure, or GCP)',
                    'Excellent communication skills - able to translate technical concepts for non-technical audiences',
                    'Entrepreneurial mindset and comfortable with ambiguity',
                    'Relevant certifications (CISSP, CISM, or equivalent) preferred',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">Why Join Us</h3>
                <p className="text-muted-foreground mb-4">
                  As a fractional CISO with our network, you'll have the variety of consulting with
                  the depth of an executive role. Work with diverse clients while we handle business
                  development, contracting, and administrative overhead.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Flexible engagement model - choose clients and time commitments that work for you',
                    'Competitive rates depending on experience and engagement complexity',
                    'Access to our methodology, templates, and tooling',
                    'Collaborative network of peer CISOs for support and knowledge sharing',
                    'Continuous professional development budget',
                    'No business development required - we bring the clients to you',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-4">
                  Apply for this Role
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Ready to join our network? Send us your CV and a brief note explaining why you'd
                  be a great fit.
                </p>
                <a href="/join" className="btn-hero w-full justify-center">
                  Apply Now <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>

              <div className="p-6 rounded-xl bg-background border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-4">Questions?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Want to learn more about the role before applying? We're happy to have an informal
                  chat.
                </p>
                <a
                  href="mailto:careers@thecisonetwork.com?subject=Question: Fractional CISO Role"
                  className="text-teal hover:underline text-sm"
                >
                  Contact our recruitment team
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
